/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generalActions, formErrorsCampaign_action } from 'store/actions';
import { manageAppsAPI } from 'services/manageApps.service';
import { campaignEnvironment } from 'constants';
import { DeleteIcon } from 'assets/icons';
import { appNumberToPromote } from 'store/selectors';

function formatDate(date) {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export const useFacade = (environment, expanded) => {
  const { data: appList } = manageAppsAPI.useFetchAppsToPromoteByFilterQuery(false);
  const { data: websiteList } = manageAppsAPI.useFetchAppsToPromoteByFilterQuery(true);
  const dispatch = useDispatch();

  const [isAppName, setIsAppName] = useState(true);
  const [isDate, setIsDate] = useState(true);
  const [errorText, setErrorText] = useState('');

  const original_cm_name = useSelector(
    (state) => state?.general?.mainCampaignObject?.original_cm_name,
  );

  const selectedCampName = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.campaignName,
  );

  const isWebsite = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.isWebsite,
  );

  const all_camp_names = useSelector(
    (state) => state?.general?.mainCampaignObject?.allUserCampaigns,
  );

  useEffect(() => {
    selectedCampName && handleChangeCampaignName(selectedCampName);
  }, [selectedCampName, all_camp_names]);

  const errorFlag = useSelector((state) => state.formErrorsCampaign.campaign_name);

  const appID = useSelector(appNumberToPromote);

  const newCampName = useMemo(() => {
    const listToSearch = isWebsite ? websiteList : appList;
    return listToSearch?.find(({ pba_id }) => appID === pba_id);
  }, [appID, appList, websiteList, isWebsite]);

  const handleChangeCampaignName = useCallback(
    (newName) => {
      let nextErrorText = '';

      if (!newName || newName.length > 100) {
        nextErrorText = newName
          ? 'Please enter a unique name (max 100 characters)'
          : 'Campaign name is required';
        dispatch(
          formErrorsCampaign_action.updateErrorField({
            errorField: 'campaign_name',
            newValue: true,
          }),
        );
      } else {
        const nameIsDuplicate = all_camp_names.some((cm) => {
          return cm === newName;
        });
        let isDuplicate = false;
        if (environment === campaignEnvironment.duplicate) {
          isDuplicate = nameIsDuplicate || original_cm_name === newName;
        }
        if (
          environment === campaignEnvironment.edit ||
          environment === campaignEnvironment.new_empty
        ) {
          isDuplicate = nameIsDuplicate;
        }
        if (isDuplicate) {
          nextErrorText = 'Duplicated campaign name must be changed. Please enter a unique name.';
        }
        dispatch(
          formErrorsCampaign_action.updateErrorField({
            errorField: 'campaign_name',
            newValue: isDuplicate,
          }),
        );
      }

      setErrorText(nextErrorText);
      dispatch(
        generalActions.updateCurrentCampaignObj({
          propField: 'campaignName',
          newValue: newName,
        }),
      );
    },
    [all_camp_names, original_cm_name, environment],
  );

  const onInputChange = useCallback(
    (e) => {
      handleChangeCampaignName(e.target.value);
    },
    [original_cm_name, all_camp_names, environment],
  );

  const deleteCampaignName = useCallback(() => {
    handleChangeCampaignName('');
  }, [handleChangeCampaignName]);

  const isRightIcon = useMemo(() => {
    return selectedCampName?.length ? <DeleteIcon /> : null;
  }, [selectedCampName]);

  const handleClickGenBtn = useCallback(
    (field, incomeValue) => {
      if (field === 'isAppName' && incomeValue !== true) {
        setIsAppName(!isAppName);
      }
      if (field === 'isAppName' && incomeValue === true) {
        setIsAppName(newCampName?.pba_name);
      }

      if (field === 'isDate' && incomeValue !== true) {
        setIsDate(!isDate);
      }
      if (field === 'isDate' && incomeValue === true) {
        setIsDate(formatDate(new Date()));
      }
    },
    [isAppName, newCampName, isDate],
  );

  useEffect(() => {
    if (!selectedCampName && expanded) {
      setIsAppName(false);
      setIsDate(false);
    }
  }, [selectedCampName]);

  useEffect(() => {
    let generatedName;
    if (!isAppName && isDate) {
      generatedName = ` - ${formatDate(new Date())}`;
    }
    if (isAppName && !isDate) {
      generatedName = `${isAppName} - `;
    }
    if (isAppName && isAppName !== true && isDate) {
      generatedName = `${isAppName} - ${formatDate(new Date())}`;
    }
    // if (!isAppName && !isDate) {
    //   console.log({ isAppName, isDate });
    //   handleChangeCampaignName('');
    // }
    generatedName &&
      dispatch(
        generalActions.updateCurrentCampaignObj({
          propField: 'campaignName',
          newValue: generatedName,
        }),
      );
  }, [isAppName, isDate]);

  useEffect(() => {
    const listReady = isWebsite ? websiteList : appList;
    if (environment === campaignEnvironment.new_empty && appID && listReady) {
      setIsAppName(newCampName?.pba_name);
      setIsDate(formatDate(new Date()));
      dispatch(
        generalActions.updateCurrentCampaignObj({
          propField: 'campaignName',
          newValue: newCampName ? `${newCampName?.pba_name} - ${formatDate(new Date())}` : '',
        }),
      );
    }
  }, [environment, appList, websiteList, appID, isWebsite, newCampName]);

  return {
    handleClickGenBtn,
    isRightIcon,
    deleteCampaignName,
    onInputChange,
    selectedCampName,
    isAppName,
    setIsAppName,
    isDate,
    setIsDate,
    newCampName,
    errorFlag,
    errorText,
  };
};
